import { z } from 'zod';
// ============================================================================
// Admin Turbopuffer Schemas
// ============================================================================

// Health Check
export const AdminTurbopufferHealthResponseSchema = z.object({
  healthy: z.boolean(),
  timestamp: z.date(),
  stats: z.object({
    recentOperations: z.number().int(),
    successRate: z.string(),
    avgLatency: z.string(),
  }),
  recentActivity: z.array(
    z.object({
      operation: z.string(),
      success: z.boolean(),
      latencyMs: z.number().nullable(),
      timestamp: z.date(),
    })
  ),
});

export type AdminTurbopufferHealthResponse = z.infer<typeof AdminTurbopufferHealthResponseSchema>;

// Stats
export const AdminTurbopufferStatsQuerySchema = z.object({
  namespace: z.string().optional(),
  timeRange: z.string().default('24h'),
  view: z.string().default('overview'),
});

export type AdminTurbopufferStatsQuery = z.infer<typeof AdminTurbopufferStatsQuerySchema>;

export const AdminTurbopufferStatsResponseSchema = z.object({
  timeRange: z.string(),
  startTime: z.date(),
  endTime: z.date(),
  aggregatedStats: z.array(z.unknown()),
  recentOperations: z.array(z.unknown()),
  errorSummary: z.array(z.unknown()),
  costBreakdown: z.unknown(),
  currentStats: z.unknown(),
});

export type AdminTurbopufferStatsResponse = z.infer<typeof AdminTurbopufferStatsResponseSchema>;

// Usage
export const AdminTurbopufferUsageQuerySchema = z.object({
  timeRange: z.string().default('24h'),
});

export type AdminTurbopufferUsageQuery = z.infer<typeof AdminTurbopufferUsageQuerySchema>;

export const AdminTurbopufferUsageResponseSchema = z.object({
  timeRange: z.string(),
  summary: z.object({
    totalOperations: z.number(),
    successRate: z.string(),
    estimatedCost: z.string(),
    documentsProcessed: z.number(),
  }),
  namespaceUsage: z.array(z.unknown()),
  operationBreakdown: z.array(z.unknown()),
  timeSeries: z.array(z.unknown()),
  cacheStats: z.unknown(),
  topUsers: z.array(z.unknown()),
});

export type AdminTurbopufferUsageResponse = z.infer<typeof AdminTurbopufferUsageResponseSchema>;

// Sync Status
export const AdminTurbopufferSyncStatusResponseSchema = z.object({
  status: z.object({
    totalTweets: z.number().int(),
    eligible: z.number().int(),
    ineligible: z.number().int(),
    syncPercentage: z.number(),
  }),
  lastSync: z.date().nullable(),
});

export type AdminTurbopufferSyncStatusResponse = z.infer<typeof AdminTurbopufferSyncStatusResponseSchema>;

// Sync Trigger
export const AdminTurbopufferSyncBodySchema = z.object({
  batchSize: z.number().int().positive().default(100),
  authorId: z.string().optional(),
  force: z.boolean().default(false),
  clearExisting: z.boolean().default(false),
  namespace: z.string().optional(),
});

export type AdminTurbopufferSyncBody = z.infer<typeof AdminTurbopufferSyncBodySchema>;

export const AdminTurbopufferSyncResponseSchema = z.object({
  message: z.string(),
  tweetsToProcess: z.number().int(),
  batchSize: z.number().int().optional(),
  namespace: z.string().optional(),
  jobQueued: z.boolean().optional(),
});

export type AdminTurbopufferSyncResponse = z.infer<typeof AdminTurbopufferSyncResponseSchema>;

// Test
export const AdminTurbopufferTestResponseSchema = z.object({
  apiKeyPresent: z.boolean(),
  apiKeyValid: z.boolean(),
  namespaceConfigured: z.boolean(),
  namespaceExists: z.boolean(),
  canWrite: z.boolean(),
  canQuery: z.boolean(),
  error: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
});

export type AdminTurbopufferTestResponse = z.infer<typeof AdminTurbopufferTestResponseSchema>;

// Reset
export const AdminTurbopufferResetBodySchema = z.object({
  namespace: z.string().optional(),
});

export type AdminTurbopufferResetBody = z.infer<typeof AdminTurbopufferResetBodySchema>;

export const AdminTurbopufferResetResponseSchema = z.object({
  success: z.boolean(),
  namespace: z.string().optional(),
});

export type AdminTurbopufferResetResponse = z.infer<typeof AdminTurbopufferResetResponseSchema>;

// Namespaces
export const AdminTurbopufferNamespacesResponseSchema = z.object({
  namespaces: z.array(z.string()),
});

export type AdminTurbopufferNamespacesResponse = z.infer<typeof AdminTurbopufferNamespacesResponseSchema>;

// Env
export const AdminTurbopufferEnvResponseSchema = z.object({
  current: z.string(),
  expected: z.string(),
  environment: z.object({
    nodeEnv: z.string(),
    devEnvMode: z.string(),
  }),
  healthy: z.boolean(),
});

export type AdminTurbopufferEnvResponse = z.infer<typeof AdminTurbopufferEnvResponseSchema>;

// Reconcile
export const AdminTurbopufferReconcileBodySchema = z.object({
  batchSize: z.number().int().positive().default(1000),
  namespace: z.string().optional(),
  since: z.string().optional(),
});

export type AdminTurbopufferReconcileBody = z.infer<typeof AdminTurbopufferReconcileBodySchema>;

export const AdminTurbopufferReconcileResponseSchema = z.object({
  ok: z.boolean(),
});

export type AdminTurbopufferReconcileResponse = z.infer<typeof AdminTurbopufferReconcileResponseSchema>;

// Sync-Status (live)
export const AdminTurbopufferSyncStatusLiveResponseSchema = z.object({
  totalTweets: z.number().int(),
  indexedTweets: z.number().int(),
  embeddedTweets: z.number().int(),
  percentIndexed: z.number(),
  percentSynced: z.number(),
  lastSyncedAt: z.date().nullable(),
  unsyncedRecent: z.number().int(),
  jobs: z.object({
    embeddings: z.object({
      queued: z.number().int(),
      running: z.number().int(),
    }),
    tweets: z.object({
      queued: z.number().int(),
      running: z.number().int(),
    }),
  }),
  currentlySyncing: z.boolean(),
});

export type AdminTurbopufferSyncStatusLiveResponse = z.infer<typeof AdminTurbopufferSyncStatusLiveResponseSchema>;
